import axios from "axios";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { AiOutlinePlusCircle } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { PaymentElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { createevent } from "../../redux/actions/event";
import { server } from "../../server";
import { IMAGE_UPLOAD_MAX_MB } from "../../uploadLimits";
import { EVENT_BANNER_SIZE_LABEL } from "../../constants/eventBanner";
import EventBannerCropModal from "./EventBannerCropModal";
import { useEventBannerUpload } from "../../hooks/useEventBannerUpload";
import {
  buildStripeDefaultBilling,
  getStripeBillingPrefillKey,
  getStripeElementsOptions,
  getStripePaymentElementOptions,
  STRIPE_CHECKOUT_PAYMENT_ELEMENT_CONTAINER_CLASS,
  cacheStripeKeyResponse,
} from "../../utils/stripePaymentUi";

const ZERO_DECIMAL_CURRENCIES = new Set(["JPY", "KRW"]);
const formatLocalDateInput = (date = new Date()) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Separate component for PaymentElement that needs clientSecret in Elements
const StripePaymentSection = ({
  currency,
  totalAmount,
  clientSecret,
  paymentMethodConfigurationId = "",
  stripePromise,
  onPaymentReady,
}) => {
  const { user } = useSelector((state) => state.user);
  const billingDefaults = useMemo(() => buildStripeDefaultBilling(null, user), [user]);
  const billingPrefillKey = getStripeBillingPrefillKey(billingDefaults);
  // Ensure stripePromise is valid - it must be a promise from loadStripe()
  if (!stripePromise) {
    return (
      <p className="text-sm text-slate-500">
        Loading payment form...
      </p>
    );
  }

  // Validate that stripePromise is actually a promise (has a .then method)
  if (typeof stripePromise.then !== 'function') {
    // Invalid stripePromise
    return (
      <p className="text-sm text-rose-600">
        Payment configuration error. Please refresh the page.
      </p>
    );
  }

  if (!clientSecret) {
    return (
      <p className="text-sm text-slate-500">
        Preparing payment form...
      </p>
    );
  }

  return (
    <Elements
      key={`${clientSecret}__${billingPrefillKey}__${paymentMethodConfigurationId}`}
      stripe={stripePromise}
      options={getStripeElementsOptions({
        clientSecret,
        billingDefaults,
        paymentMethodConfiguration: paymentMethodConfigurationId,
      })}
    >
      <StripePaymentContent
        currency={currency}
        totalAmount={totalAmount}
        clientSecret={clientSecret}
        onPaymentReady={onPaymentReady}
        defaultBillingDetails={billingDefaults}
      />
    </Elements>
  );
};

const StripePaymentContent = ({
  currency,
  totalAmount,
  clientSecret,
  onPaymentReady,
  defaultBillingDetails,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  // Pass stripe, elements, and clientSecret to parent when ready
  useEffect(() => {
    if (stripe && elements && clientSecret && onPaymentReady) {
      onPaymentReady({ stripe, elements, clientSecret });
    }
  }, [stripe, elements, clientSecret, onPaymentReady]);

  return (
    <>
      <p className="text-sm text-slate-600">
        Complete your payment securely. The charge will be{" "}
        <span className="font-semibold">
          GBP {Number(totalAmount).toFixed(2)}
        </span>
        . Payment methods configured in your Stripe dashboard will be available.
      </p>
      <div className={STRIPE_CHECKOUT_PAYMENT_ELEMENT_CONTAINER_CLASS}>
        <PaymentElement options={getStripePaymentElementOptions(defaultBillingDetails)} />
      </div>
    </>
  );
};

const CreateEventForm = ({
  stripeAvailable,
  stripePromise,
  entityLabel = "Event",
  entityLabelPlural = "Events",
  apiBasePath = `${server}/event`,
  apiNamespace = "event",
  successRedirectPath = "/profile/events",
}) => {
  const isService = entityLabel.toLowerCase() === "service";
  const settingsKey = isService ? "serviceSettings" : "eventSettings";
  const { user } = useSelector((state) => state.user);
  const { success, error } = useSelector((state) => state.events);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [bannerLink, setBannerLink] = useState("");
  const [serviceDescription, setServiceDescription] = useState("");
  const [bannerFile, setBannerFile] = useState(null);
  const {
    cropSession,
    handleFileSelect: handleBannerFileSelect,
    handleCropConfirm,
    handleCropCancel,
  } = useEventBannerUpload(setBannerFile);
  const [selectedWeeks, setSelectedWeeks] = useState(null);
  const [paymentPlan, setPaymentPlan] = useState("upfront");
  const [preferredStart, setPreferredStart] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("wallet");
  const [eventSettings, setEventSettings] = useState(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentMethodConfigurationId, setPaymentMethodConfigurationId] = useState("");
  const [paymentContext, setPaymentContext] = useState(null);
  const [discountCode, setDiscountCode] = useState("");
  const [stripeTotalAmount, setStripeTotalAmount] = useState(null);
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [servicePrice, setServicePrice] = useState("");

  const durationOptions = useMemo(() => {
    if (!eventSettings?.pricingTiers?.length) return [];
    return [...eventSettings.pricingTiers]
      .filter((tier) => tier && tier.isActive !== false)
      .map((tier) => ({
        ...tier,
        weeks: Math.max(1, Math.floor(Number(tier.weeks) || 1)),
        price: Number(tier.price) || 0,
        label: (tier.label || `${Math.max(1, Math.floor(Number(tier.weeks) || 1))} weeks`).trim(),
      }))
      .sort((a, b) => a.weeks - b.weeks);
  }, [eventSettings]);

  const weeklyRate = useMemo(() => {
    const w = Number(eventSettings?.weeklyRate);
    if (Number.isFinite(w) && w > 0) return w;
    const oneWeek = durationOptions.find((t) => t.weeks === 1);
    return Number(oneWeek?.price || 0);
  }, [eventSettings, durationOptions]);

  const selectedTier = useMemo(() => {
    if (!durationOptions.length) return null;
    const w = selectedWeeks != null ? selectedWeeks : durationOptions[0].weeks;
    return durationOptions.find((t) => t.weeks === w) || durationOptions[0];
  }, [durationOptions, selectedWeeks]);

  const durationWeeks = Number(selectedTier?.weeks || 1);
  const payWeeklyTotal = useMemo(
    () => (weeklyRate > 0 ? Number((weeklyRate * durationWeeks).toFixed(2)) : 0),
    [weeklyRate, durationWeeks]
  );
  const upfrontTotal = useMemo(
    () => Number(selectedTier?.price || 0),
    [selectedTier]
  );
  const savingsVsWeekly = useMemo(
    () => Math.max(0, Number((payWeeklyTotal - upfrontTotal).toFixed(2))),
    [payWeeklyTotal, upfrontTotal]
  );

  const totalAmount = useMemo(() => {
    if (!selectedTier) return 0;
    if (paymentPlan === "weekly") return weeklyRate;
    return upfrontTotal;
  }, [selectedTier, paymentPlan, weeklyRate, upfrontTotal]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setIsLoadingSettings(true);
        const response = await axios.get(`${server}/options/global`);
        const options = response?.data?.options;
        if (options?.[settingsKey]) {
          setEventSettings(options[settingsKey]);
          if (options[settingsKey].pricingTiers?.length) {
            const firstTier = options[settingsKey].pricingTiers
              .filter((tier) => tier && tier.isActive !== false)
              .sort((a, b) => Number(a.order || a.weeks) - Number(b.order || b.weeks))[0];
            if (firstTier) {
              setSelectedWeeks(Math.max(1, Math.floor(Number(firstTier.weeks) || 1)));
            }
          }
        }
      } catch (settingsError) {
        toast.error(
          settingsError?.response?.data?.message ||
            `Unable to load ${entityLabel.toLowerCase()} pricing. Please contact support.`
        );
      } finally {
        setIsLoadingSettings(false);
      }
    };

    fetchSettings();
  }, [entityLabel, settingsKey]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      setIsSubmitting(false);
    }
  }, [error]);

  useEffect(() => {
    // Prevent stale success from triggering toast/redirect when revisiting page.
    dispatch({ type: "eventCreateReset" });
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(`${entityLabel} created successfully!`);
      setIsSubmitting(false);
      dispatch({ type: "eventCreateReset" });
      navigate(successRedirectPath);
    }
  }, [success, navigate, dispatch, entityLabel, successRedirectPath]);

  const currency = useMemo(() => {
    // Always use GBP - force it even if tier has different currency stored
    return "GBP";
  }, []);

  const stripePlanReady = useMemo(
    () =>
      paymentMethod === "stripe" &&
      stripeAvailable &&
      selectedTier &&
      totalAmount > 0 &&
      (paymentPlan === "upfront" || paymentPlan === "weekly"),
    [paymentMethod, stripeAvailable, selectedTier, totalAmount, paymentPlan]
  );

  // Create payment intent when Stripe payment method is selected (initial load; discount applied via Apply button)
  useEffect(() => {
    if (stripePlanReady) {
      (async () => {
        try {
          const { data } = await axios.post(
            `${apiBasePath}/create-event-payment`,
            {
              durationWeeks,
              paymentPlan,
              paymentMethod: "stripe",
              currency,
              discountCode: discountCode.trim() || undefined,
            },
            { withCredentials: true }
          );
          if (data?.clientSecret) {
            setClientSecret(data.clientSecret);
            setPaymentMethodConfigurationId(data?.paymentMethodConfigurationId || "");
            setStripeTotalAmount(data.totalAmount != null ? data.totalAmount : totalAmount);
          }
        } catch (err) {
          const msg = err?.response?.data?.message || err?.message || "Could not prepare payment.";
          if (err?.response?.status === 400 && discountCode.trim()) {
            toast.error(msg);
          }
        }
      })();
    } else {
      setClientSecret(null);
      setPaymentMethodConfigurationId("");
      setStripeTotalAmount(null);
    }
  }, [stripePlanReady, paymentPlan, durationWeeks, currency, apiBasePath]);

  // Re-fetch payment intent when discount code is cleared (so amount resets to full price)
  useEffect(() => {
    if (stripePlanReady && !discountCode.trim()) {
      (async () => {
        try {
          const { data } = await axios.post(
            `${apiBasePath}/create-event-payment`,
            { durationWeeks, paymentPlan, paymentMethod: "stripe", currency },
            { withCredentials: true }
          );
          if (data?.clientSecret) {
            setClientSecret(data.clientSecret);
            setPaymentMethodConfigurationId(data?.paymentMethodConfigurationId || "");
            setStripeTotalAmount(data.totalAmount != null ? data.totalAmount : totalAmount);
          }
        } catch (_) {
          // Ignore; keep existing form
        }
      })();
    }
  }, [discountCode, stripePlanReady, paymentPlan, durationWeeks, currency, apiBasePath]);

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.info("Enter a discount code first.");
      return;
    }
    if (
      (paymentPlan !== "upfront" && paymentPlan !== "weekly") ||
      paymentMethod !== "stripe" ||
      !stripeAvailable ||
      !selectedTier ||
      totalAmount <= 0
    ) {
      return;
    }
    setApplyingDiscount(true);
    try {
      const { data } = await axios.post(
        `${apiBasePath}/create-event-payment`,
        {
          durationWeeks,
          paymentPlan,
          paymentMethod: "stripe",
          currency,
          discountCode: discountCode.trim().toUpperCase(),
        },
        { withCredentials: true }
      );
      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
        setPaymentMethodConfigurationId(data?.paymentMethodConfigurationId || "");
        setStripeTotalAmount(data.totalAmount != null ? data.totalAmount : totalAmount);
        const discounted = data.totalAmount != null && data.totalAmount < totalAmount;
        toast.success(discounted ? "Discount applied." : "Code applied.");
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "Invalid or expired discount code.";
      toast.error(msg);
    } finally {
      setApplyingDiscount(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isLoadingSettings) {
      toast.info(`${entityLabel} pricing is still loading. Please wait a moment.`);
      return;
    }

    if (!selectedTier || durationOptions.length === 0) {
      toast.error("Please choose how long the advert should run. Contact support if no options appear.");
      return;
    }
    if (paymentPlan === "weekly" && weeklyRate <= 0) {
      toast.error("Weekly payment is not set up yet. Please contact support or choose pay upfront.");
      return;
    }

    if (!bannerFile) {
      toast.error("Please upload a banner image");
      return;
    }

    if (!isService && (!preferredStart || !String(preferredStart).trim())) {
      toast.error("Please select a Starting Date");
      return;
    }

    if (!user?._id) {
      toast.error("Seller account not found. Please re-login.");
      return;
    }
    if (isService && Number(servicePrice || 0) <= 0) {
      toast.error("Please enter a valid service price.");
      return;
    }
    if (isService && !String(serviceDescription || "").trim()) {
      toast.error("Please add a service description.");
      return;
    }

    const formData = new FormData();
    formData.append("banner", bannerFile);
    formData.append("name", name || `Untitled ${entityLabel}`);
    if (isService) {
      formData.append("originalPrice", Number(servicePrice || 0));
      formData.append("discountPrice", Number(servicePrice || 0));
      formData.append("description", serviceDescription.trim());
    }
    formData.append("bannerLink", bannerLink || "");
    formData.append("shopId", user._id);
    formData.append("durationWeeks", Number(selectedTier?.weeks || 1));
    formData.append("paymentPlan", paymentPlan);
    formData.append("billingCycle", "weekly");
    const submitTotal = paymentMethod === "stripe" && stripeTotalAmount != null ? stripeTotalAmount : totalAmount;
    formData.append("totalAmount", submitTotal);
    formData.append("currency", currency);
    formData.append("paymentMethod", paymentMethod);
    if (discountCode && discountCode.trim()) {
      formData.append("discountCode", discountCode.trim().toUpperCase());
    }

    // Keep this as a date-only string to avoid timezone shifts
    // (e.g. selected 11th becoming 10th in UTC conversion).
    const preferredStartInput = isService ? formatLocalDateInput(new Date()) : String(preferredStart || "").trim();
    if (preferredStartInput) {
      formData.append("preferredStart", preferredStartInput);
    }
    setIsSubmitting(true);

    if (paymentMethod === "wallet") {
      dispatch(createevent(formData, apiNamespace));
      return;
    }

    if (paymentMethod === "stripe") {
      if (!stripeAvailable) {
        toast.error("Card payments are not available right now. Please use wallet balance.");
        setIsSubmitting(false);
        return;
      }

      // Use payment context from nested Elements wrapper
      if (!paymentContext?.stripe || !paymentContext?.elements || !paymentContext?.clientSecret) {
        toast.error("Payment system is not ready. Please wait a moment and try again.");
        setIsSubmitting(false);
        return;
      }

      (async () => {
        try {
          // Submit the form first to validate payment details
          const { error: submitError } = await paymentContext.elements.submit();
          
          if (submitError) {
            throw new Error(submitError.message || "Please check your payment details.");
          }

          // Confirm payment using PaymentElement
          const { error, paymentIntent } = await paymentContext.stripe.confirmPayment({
            elements: paymentContext.elements,
            clientSecret: paymentContext.clientSecret,
            redirect: "if_required",
          });

          if (error) {
            throw new Error(error.message || "Payment failed.");
          }

          if (paymentIntent.status !== "succeeded") {
            throw new Error("Payment not completed. Please try again.");
          }

          formData.append("paymentIntentId", paymentIntent.id);
          dispatch(createevent(formData, apiNamespace));
        } catch (paymentError) {
          setIsSubmitting(false);
          toast.error(
            paymentError?.response?.data?.message ||
              paymentError?.message ||
              "Unable to process payment."
          );
        }
      })();
      return;
    }

    setIsSubmitting(false);
    toast.error("Unsupported payment method selected. Please refresh and try again.");
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4 lg:p-6">
      <header className="mb-6">
        <h2 className="text-lg font-semibold text-slate-900">Advertise {entityLabel}</h2>
        <p className="text-sm text-slate-500">
          Promote your {entityLabel.toLowerCase()} with paid placement and admin moderation.
        </p>
      </header>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="pb-2 block font-medium text-sm text-gray-700">
            {entityLabel} Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            placeholder="Summer Skirmish"
            required
          />
        </div>

        {!isService && (
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <label className="pb-2 block font-medium text-sm text-gray-700">
                Banner Link <span className="text-gray-400">(opens in new window)</span>
              </label>
              <input
                type="url"
                name="bannerLink"
                value={bannerLink}
                onChange={(e) => setBannerLink(e.target.value)}
                className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="https://your-link.com"
              />
            </div>
            <div>
              <label className="pb-2 block font-medium text-sm text-gray-700">
                Starting Date <span className="text-red-500">*</span>{" "}
                <span className="text-gray-400">(when this will start showing)</span>
              </label>
              <input
                type="date"
                value={preferredStart}
                onChange={(e) => setPreferredStart(e.target.value)}
                className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                min={new Date().toISOString().slice(0, 10)}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This will start showing on this date once approved.
              </p>
            </div>
          </div>
        )}

        {isService && (
          <div>
            <label className="pb-2 block font-medium text-sm text-gray-700">
              Service Description <span className="text-red-500">*</span>
            </label>
            <textarea
              name="serviceDescription"
              value={serviceDescription}
              onChange={(e) => setServiceDescription(e.target.value)}
              className="appearance-none block w-full min-h-[96px] px-3 py-2 border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Tell customers what your service includes..."
              maxLength={2000}
              required
            />
          </div>
        )}

        {isService && (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2">
            <div>
              <label className="pb-2 block font-medium text-sm text-gray-700">
                Service Price <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={servicePrice}
                onChange={(e) => setServicePrice(e.target.value)}
                className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="49.99"
                required
              />
            </div>
            <div>
              <label className="pb-2 block font-medium text-sm text-gray-700">
                How long should this advert run? <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedWeeks ?? ""}
                onChange={(e) => setSelectedWeeks(Number(e.target.value))}
                className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isLoadingSettings || durationOptions.length === 0}
                required
              >
                {durationOptions.length === 0 && <option value="">No pricing available</option>}
                {durationOptions.map((tier) => (
                  <option key={tier.weeks} value={tier.weeks}>
                    {tier.label} — {tier.weeks} {tier.weeks === 1 ? "week" : "weeks"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div>
          <label className="pb-2 block font-medium text-sm text-gray-700">
            {isService ? "Service Image" : "Upload Banner"} <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept="image/*"
            id="event-banner-upload"
            className="hidden"
            onChange={handleBannerFileSelect}
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum upload size: {IMAGE_UPLOAD_MAX_MB}MB per image. Required banner size:{" "}
            {EVENT_BANNER_SIZE_LABEL}. Other sizes will open a crop tool.
          </p>
          <div className="w-full flex items-center flex-wrap gap-3">
            <label htmlFor="event-banner-upload" className="cursor-pointer">
              <AiOutlinePlusCircle size={30} className="mt-3" color="#555" />
            </label>
            {bannerFile ? (
              <img
                src={URL.createObjectURL(bannerFile)}
                alt="Banner preview"
                className="h-[120px] w-[220px] object-contain rounded"
              />
            ) : (
              <span className="text-sm text-gray-400">Select an image to preview</span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {!isService && (
            <div>
              <label className="pb-2 block font-medium text-sm text-gray-700">
                How long should this advert run?{" "}
                <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedWeeks ?? ""}
                onChange={(e) => setSelectedWeeks(Number(e.target.value))}
                className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                disabled={isLoadingSettings || durationOptions.length === 0}
                required
              >
                {durationOptions.length === 0 && <option value="">No pricing available</option>}
                {durationOptions.map((tier) => (
                  <option key={tier.weeks} value={tier.weeks}>
                    {tier.label} — {tier.weeks} {tier.weeks === 1 ? "week" : "weeks"}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <p className="pb-3 block font-medium text-sm text-gray-700">
              How do you want to pay? <span className="text-red-500">*</span>
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <label
                className={`relative block cursor-pointer rounded-xl border-2 bg-white p-4 shadow-sm transition-all duration-200 hover:shadow-md ${
                  paymentPlan === "upfront"
                    ? "border-[#38513b] ring-2 ring-[#38513b]/20 shadow-md"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <input
                  type="radio"
                  name="paymentPlan"
                  className="sr-only"
                  checked={paymentPlan === "upfront"}
                  onChange={() => setPaymentPlan("upfront")}
                />
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-slate-900 text-sm leading-snug">
                      Pay upfront
                    </span>
                    {paymentPlan === "upfront" && (
                      <span className="shrink-0 rounded-full bg-[#38513b] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    One payment of{" "}
                    <span className="font-semibold text-slate-800">GBP {upfrontTotal.toFixed(2)}</span> for the
                    full run.
                    {savingsVsWeekly > 0.009 ? (
                      <span className="mt-1.5 block rounded-md bg-emerald-50 px-2 py-1.5 text-emerald-800 border border-emerald-100">
                        Save GBP {savingsVsWeekly.toFixed(2)} vs paying every week
                      </span>
                    ) : null}
                  </p>
                </div>
              </label>

              <label
                className={`relative block rounded-xl border-2 bg-white p-4 shadow-sm transition-all duration-200 ${
                  weeklyRate <= 0
                    ? "cursor-not-allowed opacity-60 pointer-events-none"
                    : `cursor-pointer hover:shadow-md ${
                        paymentPlan === "weekly"
                          ? "border-[#38513b] ring-2 ring-[#38513b]/20 shadow-md"
                          : "border-slate-200 hover:border-slate-300"
                      }`
                }`}
              >
                <input
                  type="radio"
                  name="paymentPlan"
                  className="sr-only"
                  checked={paymentPlan === "weekly"}
                  onChange={() => setPaymentPlan("weekly")}
                  disabled={weeklyRate <= 0}
                />
                <div className="flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-slate-900 text-sm leading-snug">Pay weekly</span>
                    {paymentPlan === "weekly" && weeklyRate > 0 && (
                      <span className="shrink-0 rounded-full bg-[#38513b] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed">
                    <span className="font-semibold text-slate-800">GBP {weeklyRate.toFixed(2)}</span> per week. Over{" "}
                    {durationWeeks} weeks that would be{" "}
                    <span className="font-semibold text-slate-800">GBP {payWeeklyTotal.toFixed(2)}</span> in total.
                    {durationWeeks > 1 ? (
                      <span className="mt-1.5 block text-slate-500">
                        Wallet or card—weekly billing after approval.
                      </span>
                    ) : null}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <label className="pb-2 block font-medium text-sm text-gray-700">
                Payment method <span className="text-red-500">*</span>
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="appearance-none block w-full px-3 h-[40px] border border-gray-300 rounded-[3px] focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="wallet">Wallet balance</option>
                {stripeAvailable && (paymentPlan === "upfront" || paymentPlan === "weekly") && (
                  <option value="stripe">Card (Stripe)</option>
                )}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {paymentPlan === "weekly"
                  ? durationWeeks > 1
                    ? "First week today; then weekly from wallet or saved card once approved."
                    : "Single-week run: pay this week only."
                  : "Pay once for the full campaign with wallet or card."}
              </p>
            </div>
          </div>
        </div>

        {paymentMethod === "stripe" && (paymentPlan === "upfront" || paymentPlan === "weekly") && (
          <div className="space-y-2 rounded-md border border-slate-200 p-4">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleApplyDiscount())}
                placeholder="Discount code (optional)"
                className="w-full min-w-0 flex-1 px-3 py-2 border border-gray-300 rounded-[3px] text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={handleApplyDiscount}
                disabled={applyingDiscount}
                className="w-full sm:w-auto px-4 py-2 rounded-[3px] bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {applyingDiscount ? "Applying…" : "Apply Discount"}
              </button>
            </div>
            {paymentPlan === "weekly" && durationWeeks > 1 ? (
              <p className="text-xs text-slate-600 mb-2">
                First charge today; same card weekly after approval ({durationWeeks} weeks).
              </p>
            ) : null}
            {stripeAvailable && clientSecret && stripePromise ? (
              <StripePaymentSection
                currency={currency}
                totalAmount={stripeTotalAmount != null ? stripeTotalAmount : totalAmount}
                clientSecret={clientSecret}
                paymentMethodConfigurationId={paymentMethodConfigurationId}
                stripePromise={stripePromise}
                onPaymentReady={setPaymentContext}
              />
            ) : stripeAvailable ? (
              <p className="text-sm text-slate-500">
                Preparing payment form...
              </p>
            ) : (
              <p className="text-sm text-rose-600">
                Stripe is not configured yet. Please use wallet payment.
              </p>
            )}
          </div>
        )}

        <div className="rounded-md bg-slate-50 border border-slate-200 p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Length</span>
            <span>
              {durationWeeks} {durationWeeks === 1 ? "week" : "weeks"}
              {selectedTier?.label ? ` (${selectedTier.label})` : ""}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-600">Payment choice</span>
            <span className="text-right max-w-[60%]">
              {paymentPlan === "upfront"
                ? "One payment for the full period"
                : `Pay weekly (GBP ${weeklyRate.toFixed(2)} each week)`}
            </span>
          </div>
          {paymentPlan === "upfront" && savingsVsWeekly > 0.009 && (
            <div className="flex justify-between text-sm text-emerald-700">
              <span>You save vs paying weekly</span>
              <span>GBP {savingsVsWeekly.toFixed(2)}</span>
            </div>
          )}
          {paymentPlan === "weekly" && (
            <div className="flex justify-between text-xs text-slate-500">
              <span>Total if you paid weekly all weeks</span>
              <span>GBP {payWeeklyTotal.toFixed(2)}</span>
            </div>
          )}
          {paymentMethod === "stripe" &&
            (paymentPlan === "upfront" || paymentPlan === "weekly") &&
            stripeTotalAmount != null &&
            stripeTotalAmount < totalAmount && (
            <div className="flex justify-between text-sm text-green-700">
              <span>Discount applied</span>
              <span>- GBP {Number(totalAmount - stripeTotalAmount).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-semibold text-[#38513B] pt-2 border-t">
            <span>Total due today</span>
            <span>
              GBP{" "}
              {Number(
                paymentMethod === "stripe" && stripeTotalAmount != null
                  ? stripeTotalAmount
                  : totalAmount
              ).toFixed(2)}
            </span>
          </div>
        </div>

        <div className="rounded-md bg-amber-50 border border-amber-200 p-4 space-y-2">
          <p className="text-sm font-medium text-amber-800">
            ⏱️ Approval Notice
          </p>
          <p className="text-sm text-amber-700">
            {entityLabel} will take up to 7 days for approval. If urgent, please contact us at{" "}
            <a href="mailto:info@veteranairsoft.com" className="underline font-medium">
              info@veteranairsoft.com
            </a>
          </p>
          <p className="text-sm text-amber-700 font-medium mt-2">
            📌 Important: All {entityLabelPlural.toLowerCase()} should be relevant to veterans or airsoft.
          </p>
        </div>

        <div>
          <input
            type="submit"
            value={
              isLoadingSettings
                ? "Loading..."
                : isSubmitting
                ? "Processing..."
                : `Advertise ${entityLabel}`
            }
            disabled={isLoadingSettings || isSubmitting}
            className="cursor-pointer text-center block w-full px-3 h-[45px] border border-gray-300 rounded-[3px] bg-[#38513B] text-white font-semibold hover:opacity-90 transition disabled:opacity-60"
          />
        </div>
      </form>

      <EventBannerCropModal
        open={Boolean(cropSession)}
        imageSrc={cropSession?.src}
        originalFileName={cropSession?.fileName}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    </div>
  );
};

const CreateEvent = ({
  entityLabel = "Event",
  entityLabelPlural = "Events",
  apiBasePath = `${server}/event`,
  apiNamespace = "event",
  successRedirectPath = "/profile/events",
}) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeReady, setStripeReady] = useState(false);

  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        const { data } = await axios.get(`${server}/payment/stripe/key`, {
          withCredentials: true,
        });
        cacheStripeKeyResponse(data);
        if (data?.publishableKey && typeof data.publishableKey === 'string' && data.publishableKey.trim()) {
          const promise = loadStripe(data.publishableKey.trim());
          // Verify it's actually a promise
          if (promise && typeof promise.then === 'function') {
            setStripePromise(promise);
          } else {
            // loadStripe did not return a valid promise
            setStripePromise(null);
          }
        } else {
          setStripePromise(null);
        }
      } catch (error) {
        // Failed to load Stripe key
        setStripePromise(null);
      } finally {
        setStripeReady(true);
      }
    };

    fetchStripeKey();
  }, []);

  if (!stripeReady) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 text-center text-slate-500">
        Loading payment options...
      </div>
    );
  }

  // Don't wrap in Elements here - StripePaymentSection will create its own Elements wrapper with clientSecret
  // Only pass stripePromise if it's a valid promise
  const validStripePromise = stripePromise && typeof stripePromise.then === 'function' ? stripePromise : null;
  return (
    <CreateEventForm
      stripeAvailable={!!validStripePromise}
      stripePromise={validStripePromise}
      entityLabel={entityLabel}
      entityLabelPlural={entityLabelPlural}
      apiBasePath={apiBasePath}
      apiNamespace={apiNamespace}
      successRedirectPath={successRedirectPath}
    />
  );
};

export default CreateEvent;